import { Routes } from '@angular/router';
import { permissionGuard } from '../guards/permission.guard';
import { dineInGuard } from '../guards/dine-in.guard';
import { deliveryGuard } from '../guards/delivery.guard';
import { FeatureFlagGuard } from '../guards/feature-flag.guard';
import { assignPrGuard } from '../guards/assign-pr.guard';
import { defaultPageGuard } from '../guards/default-page.guard';

export const mainRoutes: Routes = [
   {
      // Landing page after login — per role, set in Role Permissions.
      path: '',
      pathMatch: 'full',
      canActivate: [defaultPageGuard],
      children: [],
   },
   {
      path: 'dashboard',
      loadComponent: () =>
         import('./dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
         ),
      // Dashboard is accessible to all authenticated users
      data: { title: 'Dashboard', titleKH: 'ផ្ទាំងគ្រប់គ្រង' },
   },
   {
      path: 'products',
      loadComponent: () =>
         import('./product/product.component').then((m) => m.ProductComponent),
      canActivate: [permissionGuard('view_products'), FeatureFlagGuard],
      data: { title: 'Products', titleKH: 'ផលិតផល', featureKey: 'core_pos' },
   },
   {
      path: 'products/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('view_products')],
      data: {
         title: 'Product Activity Log',
         titleKH: 'ប្រវត្តិសកម្មភាពផលិតផល',
         sourceFilter: 'Product',
      },
   },
   {
      path: 'customers',
      loadComponent: () =>
         import('./customers/customers.component').then(
            (m) => m.CustomersComponent,
         ),
      canActivate: [permissionGuard('view_customers'), FeatureFlagGuard],
      data: {
         title: 'Customers',
         titleKH: 'ផ្ទាំងអតិថិជន',
         featureKey: 'customers',
      },
   },
   {
      path: 'customers/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('view_customers')],
      data: {
         title: 'Customer Activity Log',
         titleKH: 'ប្រវត្តិអតិថិជន',
         sourceFilter: 'Customer',
      },
   },
   {
      path: 'bottle-keep',
      loadComponent: () =>
         import('./bottle-keep/bottle-keep.component').then(
            (m) => m.BottleKeepComponent,
         ),
      canActivate: [permissionGuard('view_reports'), FeatureFlagGuard],
      data: {
         title: 'Bottle Keeping',
         titleKH: 'ការផ្ញើស្រា',
         featureKey: 'bottle_keep',
      },
   },
   {
      path: 'bottle-keep/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('view_reports')],
      data: {
         title: 'Bottle Keep Log',
         titleKH: 'ប្រវត្តិផ្ញើដប',
         sourceFilter: 'Bottle Keep',
      },
   },
   {
      path: 'tables',
      loadComponent: () =>
         import('./tables/tables.component').then((m) => m.TablesComponent),
      canActivate: [
         permissionGuard('manage_tables'),
         dineInGuard,
         FeatureFlagGuard,
      ],
      data: { title: 'Tables', titleKH: 'គ្រប់គ្រងតុ', featureKey: 'core_pos' },
   },
   {
      path: 'reservations',
      loadComponent: () =>
         import('./reservations/reservations.component').then(
            (m) => m.ReservationsComponent,
         ),
      canActivate: [
         permissionGuard('view_reservations'),
         dineInGuard,
         FeatureFlagGuard,
      ],
      data: {
         title: 'Reservations',
         titleKH: 'ការកក់តុ',
         featureKey: 'reservations',
      },
   },
   {
      path: 'reservations/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('view_reservations')],
      data: {
         title: 'Reservation Log',
         titleKH: 'ប្រវត្តិនៃការកក់តុ',
         sourceFilter: 'Reservation',
      },
   },
   {
      path: 'assign-pr',
      loadComponent: () =>
         // Triggering a rebuild to fix potential watcher issue
         import('./assign-pr/assign-pr.component').then(
            (m) => m.AssignPrComponent,
         ),
      canActivate: [assignPrGuard, FeatureFlagGuard],
      data: {
         title: 'Assign PR',
         titleKH: 'ចាត់តាំង PR',
         featureKey: 'pr_management',
      },
   },
   {
      path: 'unit-type',
      loadComponent: () =>
         import('./unit-type/unit-type.component').then(
            (m) => m.UnitTypeComponent,
         ),
      canActivate: [permissionGuard('view_unit_type'), FeatureFlagGuard],
      data: {
         title: 'Unit Type',
         titleKH: 'ប្រភេទឯកតា',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'promotions',
      loadComponent: () =>
         import('./promotions/promotions.component').then(
            (m) => m.PromotionsComponent,
         ),
      canActivate: [permissionGuard('view_promotions'), FeatureFlagGuard],
      data: {
         title: 'Promotions',
         titleKH: 'កម្មវិធីពិសេស',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'promotions/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('view_promotions')],
      data: {
         title: 'Promotion Activity Log',
         titleKH: 'ប្រវត្តិសកម្មភាពកម្មវិធីពិសេស',
         sourceFilter: 'Promotion',
      },
   },
   {
      path: 'categories',
      loadComponent: () =>
         import('./categories/categories.component').then(
            (m) => m.CategoriesComponent,
         ),
      canActivate: [permissionGuard('view_categories'), FeatureFlagGuard],
      data: {
         title: 'Categories',
         titleKH: 'ប្រភេទផលិតផល',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'categories/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('view_categories')],
      data: {
         title: 'Category Activity Log',
         titleKH: 'ប្រវត្តិប្រភេទផលិតផល',
         sourceFilter: 'Category',
      },
   },
   {
      path: 'vendors',
      loadComponent: () =>
         import('./vendors/vendors.component').then((m) => m.VendorsComponent),
      canActivate: [permissionGuard('manage_vendors'), FeatureFlagGuard],
      data: {
         title: 'Vendors',
         titleKH: 'គ្រប់គ្រងអ្នកលក់',
         featureKey: 'inventory',
      },
   },
   {
      path: 'stockin',
      loadComponent: () =>
         import('./stockin/stockin.component').then((m) => m.StockinComponent),
      canActivate: [permissionGuard('stock_in'), FeatureFlagGuard],
      data: { title: 'Stock In', titleKH: 'ចូលស្តុក', featureKey: 'inventory' },
   },
   {
      path: 'stockout',
      loadComponent: () =>
         import('./stockout/stockout.component').then(
            (m) => m.StockoutComponent,
         ),
      canActivate: [permissionGuard('stock_out'), FeatureFlagGuard],
      data: {
         title: 'Stock Out',
         titleKH: 'ចេញស្តុក',
         featureKey: 'inventory',
      },
   },
   {
      path: 'check-stock',
      loadComponent: () =>
         import('./check-stock/check-stock.component').then(
            (m) => m.CheckStockComponent,
         ),
      canActivate: [permissionGuard('view_check_stock'), FeatureFlagGuard],
      data: {
         title: 'Check Stock',
         titleKH: 'ពិនិត្យស្តុក',
         featureKey: 'inventory',
      },
   },
   {
      path: 'stock-report',
      loadComponent: () =>
         import('./stock-report/stock-report.component').then(
            (m) => m.StockReportComponent,
         ),
      canActivate: [permissionGuard('view_check_stock'), FeatureFlagGuard],
      data: {
         title: 'Stock Report',
         titleKH: 'របាយការណ៍ស្តុក',
         featureKey: 'inventory',
      },
   },
   {
      path: 'materials',
      loadComponent: () =>
         import('./materials/materials.component').then(
            (m) => m.MaterialsComponent,
         ),
      canActivate: [permissionGuard('manage_materials'), FeatureFlagGuard],
      data: {
         title: 'Materials',
         titleKH: 'សម្ភារៈ',
         featureKey: 'material_stock',
      },
   },
   {
      path: 'material-stockin',
      loadComponent: () =>
         import('./material-stockin/material-stockin.component').then(
            (m) => m.MaterialStockinComponent,
         ),
      canActivate: [permissionGuard('stock_in'), FeatureFlagGuard],
      data: {
         title: 'Material Stock In',
         titleKH: 'ចូលស្តុកសម្ភារៈ',
         featureKey: 'material_stock',
      },
   },
   {
      path: 'material-stockout',
      loadComponent: () =>
         import('./material-stockout/material-stockout.component').then(
            (m) => m.MaterialStockoutComponent,
         ),
      canActivate: [permissionGuard('stock_out'), FeatureFlagGuard],
      data: {
         title: 'Material Stock Out',
         titleKH: 'ចេញស្តុកសម្ភារៈ',
         featureKey: 'material_stock',
      },
   },
   // Legacy redirects or keep them?
   // Actually better to move them to children of settings
   {
      path: 'digital-menu',
      loadComponent: () =>
         import('./menu/menu.component').then((m) => m.MenuComponent),
      canActivate: [permissionGuard('manage_menu'), FeatureFlagGuard],
      data: { title: 'Digital Menu', titleKH: 'Digital Menu', featureKey: 'menu' },
   },
   {
      path: 'kot-display-management',
      loadComponent: () =>
         import('./kot-display-management/kot-display-management.component').then(
            (m) => m.KotDisplayManagementComponent,
         ),
      canActivate: [permissionGuard('manage_kitchen_kot'), FeatureFlagGuard],
      data: {
         title: 'KOT Display',
         titleKH: 'ការកំណត់ KOT',
         featureKey: 'kot_display',
      },
   },
   {
      path: 'hr',
      loadComponent: () =>
         import('./hr/hr.component').then((m) => m.HrComponent),
      canActivate: [permissionGuard('view_employees'), FeatureFlagGuard],
      data: {
         title: 'HR Management',
         titleKH: 'ធនធានមនុស្ស',
         featureKey: 'hr',
      },
      children: [
         { path: '', redirectTo: 'employees', pathMatch: 'full' },
         {
            path: 'employees',
            loadComponent: () =>
               import('./hr/employees/employees.component').then(
                  (m) => m.EmployeesComponent,
               ),
            canActivate: [permissionGuard('view_employees')],
            data: { title: 'Employees', titleKH: 'គ្រប់គ្រងបុគ្គលិក' },
         },
         {
            path: 'attendance',
            loadComponent: () =>
               import('./hr/attendance/attendance.component').then(
                  (m) => m.AttendanceComponent,
               ),
            canActivate: [permissionGuard('view_attendance')],
            data: { title: 'Attendance', titleKH: 'គ្រប់គ្រងវត្តមាន' },
         },
         {
            path: 'payroll',
            loadComponent: () =>
               import('./hr/payroll/hr-payroll.component').then(
                  (m) => m.HrPayrollComponent,
               ),
            canActivate: [permissionGuard('view_payroll'), FeatureFlagGuard],
            data: {
               title: 'Payroll',
               titleKH: 'គ្រប់គ្រងប្រាក់បៀវត្ស',
               featureKey: 'hr_payroll',
            },
         },
         {
            path: 'attendance-report',
            loadComponent: () =>
               import('./hr/attendance-report/attendance-report.component').then(
                  (m) => m.AttendanceReportComponent,
               ),
            canActivate: [permissionGuard('view_attendance')],
            data: { title: 'Attendance Report', titleKH: 'របាយការណ៍វត្តមាន' },
         },
         {
            path: 'leave-requests',
            loadComponent: () =>
               import('./hr/leave-requests/leave-requests.component').then(
                  (m) => m.LeaveRequestsComponent,
               ),
            canActivate: [permissionGuard('view_attendance')],
            data: {
               title: 'Leave Requests',
               titleKH: 'ការស្នើសុំច្បាប់ឈប់សម្រាក',
            },
         },
         {
            path: 'late-excuse-requests',
            loadComponent: () =>
               import(
                  './hr/late-excuse-requests/late-excuse-requests.component'
               ).then((m) => m.LateExcuseRequestsComponent),
            canActivate: [permissionGuard('view_attendance'), FeatureFlagGuard],
            data: {
               title: 'Late Excuse Requests',
               titleKH: 'ការស្នើសុំលើកលែងការមកយឺត',
               featureKey: 'hr_payroll',
            },
         },
         {
            path: 'early-leave-requests',
            loadComponent: () =>
               import(
                  './hr/early-leave-requests/early-leave-requests.component'
               ).then((m) => m.EarlyLeaveRequestsComponent),
            canActivate: [permissionGuard('view_attendance'), FeatureFlagGuard],
            data: {
               title: 'Early Leave Requests',
               titleKH: 'ការស្នើសុំចេញមុនម៉ោង',
               featureKey: 'hr_payroll',
            },
         },
         {
            path: 'day-off-schedule',
            loadComponent: () =>
               import('./hr/day-off-schedule/day-off-schedule.component').then(
                  (m) => m.DayOffScheduleComponent,
               ),
            canActivate: [permissionGuard('view_attendance')],
            data: {
               title: 'Day-Off Schedule',
               titleKH: 'កាលវិភាគថ្ងៃឈប់សម្រាក',
            },
         },
         {
            path: 'salary-advances',
            loadComponent: () =>
               import('./hr/salary-advances/salary-advances.component').then(
                  (m) => m.SalaryAdvancesComponent,
               ),
            canActivate: [permissionGuard('view_payroll'), FeatureFlagGuard],
            data: {
               title: 'Salary Advances',
               titleKH: 'បុរេប្រទានប្រាក់ខែ',
               featureKey: 'hr_payroll',
            },
         },
      ],
   },
   {
      path: 'pr-management',
      loadComponent: () =>
         import('./pr-management/pr-management.component').then(
            (m) => m.PrManagementComponent,
         ),
      canActivate: [permissionGuard('view_pr_management'), FeatureFlagGuard],
      data: {
         title: 'PR Management',
         titleKH: 'ការគ្រប់គ្រង PR',
         featureKey: 'pr_management',
      },
      children: [
         { path: '', redirectTo: 'profile', pathMatch: 'full' },
         {
            path: 'profile',
            loadComponent: () =>
               import('./pr-management/pr-profile/pr-profile.component').then(
                  (m) => m.PrProfileComponent,
               ),
            canActivate: [permissionGuard('view_pr_management')],
            data: { title: 'PR Profile', titleKH: 'ព័ត៌មាន PR' },
         },
         {
            path: 'commission-setup',
            loadComponent: () =>
               import('./pr-management/pr-commission-setup/pr-commission-setup.component').then(
                  (m) => m.PrCommissionSetupComponent,
               ),
            canActivate: [permissionGuard('manage_pr_setup')],
            data: { title: 'Commission Setup', titleKH: 'ការកំណត់កម្រៃជើងសារ' },
         },
         {
            path: 'commission-report',
            loadComponent: () =>
               import('./pr-management/pr-commission-report/pr-commission-report.component').then(
                  (m) => m.PrCommissionReportComponent,
               ),
            canActivate: [permissionGuard('view_pr_management')],
            data: {
               title: 'PR Commission Report',
               titleKH: 'របាយការណ៍កម្រៃជើងសារ',
            },
         },
      ],
   },
   {
      path: 'users',
      loadComponent: () =>
         import('./users/user.component').then((m) => m.UserComponent),
      canActivate: [permissionGuard('view_employees'), FeatureFlagGuard],
      data: {
         title: 'User Management',
         titleKH: 'គ្រប់គ្រងអ្នកប្រើប្រាស់',
         featureKey: 'user_management',
      },
   },
   {
      path: 'users/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('view_employees')],
      data: {
         title: 'Team Activity Log',
         titleKH: 'ប្រវត្តិនៃសកម្មភាពបុគ្គលិក',
         sourceFilter: 'Employee',
      },
   },
   {
      path: 'permission',
      loadComponent: () =>
         import('./permission/permission.component').then(
            (m) => m.PermissionComponent,
         ),
      canActivate: [permissionGuard('manage_permissions'), FeatureFlagGuard],
      data: {
         title: 'Permission',
         titleKH: 'Permission',
         featureKey: 'permission',
      },
   },
   {
      path: 'overrides',
      loadComponent: () =>
         import('./overrides/overrides.component').then(
            (m) => m.OverridesComponent,
         ),
      canActivate: [permissionGuard('manage_permissions'), FeatureFlagGuard],
      data: {
         title: 'Per-Person Overrides',
         titleKH: 'ករណីលើកលែងតាមបុគ្គល',
         featureKey: 'permission',
      },
   },
   {
      path: 'subscription',
      loadComponent: () =>
         import('./subscription/subscription.component').then(
            (m) => m.SubscriptionComponent,
         ),
      // Subscription is accessible to all authenticated users (no permission guard)
      // This allows any user to pay for subscriptions to keep the store active
      data: { title: 'Subscription', titleKH: 'Subscription' },
   },
   {
      path: 'telegram-notification',
      loadComponent: () =>
         import('./telegram-notification/telegram-notification.component').then(
            (m) => m.TelegramNotificationComponent,
         ),
      canActivate: [permissionGuard('edit_settings'), FeatureFlagGuard],
      data: {
         title: 'Telegram Notification',
         titleKH: 'ការជូនដំណឹង Telegram',
         featureKey: 'telegram_notifications',
      },
   },
   {
      path: 'manual-approval',
      loadComponent: () =>
         import('./subscription/manual-approval/manual-approval.component').then(
            (m) => m.ManualApprovalComponent,
         ),
      canActivate: [permissionGuard('edit_settings')],
      data: { title: 'Manual Approval', titleKH: 'អនុម័តដោយដៃ' },
   },
   {
      path: 'daily-sales',
      loadComponent: () =>
         import('./reports/daily-sales/daily-sales.component').then(
            (m) => m.DailySalesComponent,
         ),
      canActivate: [permissionGuard('view_daily_sales'), FeatureFlagGuard],
      data: {
         title: 'Daily Sales Reports',
         titleKH: 'ការលក់ប្រចាំថ្ងៃ',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'shift-reports',
      loadComponent: () =>
         import('./reports/shift-reports/shift-reports.component').then(
            (m) => m.ShiftReportsComponent,
         ),
      canActivate: [permissionGuard('view_shift_reports'), FeatureFlagGuard],
      data: {
         title: 'Shifts Reports',
         titleKH: 'របាយការណ៍វេន',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'profit-loss',
      loadComponent: () =>
         import('./reports/profit-loss/profit-loss.component').then(
            (m) => m.ProfitLossComponent,
         ),
      canActivate: [permissionGuard('view_profit_loss'), FeatureFlagGuard],
      data: {
         title: 'Profit & Loss',
         titleKH: 'ចំណេញ និងខាត',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'cashier-performance',
      loadComponent: () =>
         import('./reports/cashier-performance/cashier-performance.component').then(
            (m) => m.CashierPerformanceComponent,
         ),
      canActivate: [permissionGuard('view_cashier_performance'), FeatureFlagGuard],
      data: {
         title: 'Cashier Performance',
         titleKH: 'ការអនុវត្តបេឡាករ',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'bill-list',
      loadComponent: () =>
         import('./reports/bill-list/bill-list.component').then(
            (m) => m.BillListComponent,
         ),
      canActivate: [permissionGuard('view_bill_list'), FeatureFlagGuard],
      data: {
         title: 'Bill List',
         titleKH: 'បញ្ជីវិក្កយបត្រ',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'sales-by-order-type',
      loadComponent: () =>
         import('./reports/sales-by-order-type/sales-by-order-type.component').then(
            (m) => m.SalesByOrderTypeComponent,
         ),
      canActivate: [permissionGuard('view_sales_by_order_type'), FeatureFlagGuard],
      data: {
         title: 'Sales by Order Type',
         titleKH: 'ការលក់តាមប្រភេទបញ្ជាទិញ',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'voids-discounts-audit',
      loadComponent: () =>
         import('./reports/voids-discounts-audit/voids-discounts-audit.component').then(
            (m) => m.VoidsDiscountsAuditComponent,
         ),
      canActivate: [permissionGuard('view_voids_discounts_audit'), FeatureFlagGuard],
      data: {
         title: 'Voids & Discounts Audit',
         titleKH: 'សវនកម្មការលុបចោល និងបញ្ចុះតម្លៃ',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'categories-reports',
      loadComponent: () =>
         import('.//reports/categories-reports/categories-reports.component').then(
            (m) => m.CategoriesReportsComponent,
         ),
      canActivate: [permissionGuard('view_categories_reports'), FeatureFlagGuard],
      data: {
         title: 'Sales By Categories',
         titleKH: 'ប្រភេទផលិតផល',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'best-selling-products',
      loadComponent: () =>
         import('./reports/best-selling-products-reports/best-selling-products-reports.component').then(
            (m) => m.BestSellingProductsReportsComponent,
         ),
      canActivate: [permissionGuard('view_best_selling'), FeatureFlagGuard],
      data: {
         title: 'Best Selling Products',
         titleKH: 'ផលិតផលលក់ដាច់',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'no-sales-products',
      loadComponent: () =>
         import('./reports/no-sales-products-reports/no-sales-products-reports.component').then(
            (m) => m.NoSalesProductsReportsComponent,
         ),
      canActivate: [permissionGuard('view_no_sales_products'), FeatureFlagGuard],
      data: {
         title: 'Products with No Sales',
         titleKH: 'ផលិតផលគ្មានការលក់',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'payment-options',
      loadComponent: () =>
         import('./reports/payments-reports/payments-reports.component').then(
            (m) => m.PaymentsReportsComponent,
         ),
      canActivate: [permissionGuard('view_payments_report'), FeatureFlagGuard],
      data: {
         title: 'Payment Reports',
         titleKH: 'ប្រភេទការបង់ប្រាក់',
         featureKey: 'core_pos',
      },
   },
   {
      path: 'reservations-report',
      loadComponent: () =>
         import('./reports/reservations-report/reservations-report.component').then(
            (m) => m.ReservationsReportComponent,
         ),
      canActivate: [permissionGuard('view_reservations_report'), FeatureFlagGuard],
      data: {
         title: 'Reservations Report',
         titleKH: 'របាយការណ៍ការកក់តុ',
         featureKey: 'core_pos',
      },
   },

   {
      path: 'knowledge-hub',
      loadComponent: () =>
         import('./knowledge-hub/knowledge-hub.component').then(
            (m) => m.KnowledgeHubComponent,
         ),
      canActivate: [FeatureFlagGuard],
      data: {
         title: 'Knowledge Hub',
         titleKH: 'មជ្ឈមណ្ឌលចំណេះដឹង',
         featureKey: 'knowledge_hub',
      },
   },
   // Accounting routes
   {
      path: 'accounting-setup',
      loadComponent: () =>
         import('./accounting/accounting-setup/accounting-setup-wizard.component').then(
            (m) => m.AccountingSetupWizardComponent,
         ),
      canActivate: [permissionGuard('manage_accounting'), FeatureFlagGuard],
      data: {
         title: 'Accounting Setup',
         titleKH: 'ការតំឡើងគណនេយ្យ',
         featureKey: 'accounting',
      },
   },
   {
      path: 'chart-of-accounts',
      loadComponent: () =>
         import('./accounting/chart-of-accounts/chart-of-accounts.component').then(
            (m) => m.ChartOfAccountsComponent,
         ),
      canActivate: [permissionGuard('view_accounting'), FeatureFlagGuard],
      data: {
         title: 'Chart of Accounts',
         titleKH: 'គណនីគណនេយ្យ',
         featureKey: 'accounting',
      },
   },
   {
      path: 'journal-entries',
      loadComponent: () =>
         import('./accounting/journal-entries/journal-entries.component').then(
            (m) => m.JournalEntriesComponent,
         ),
      canActivate: [permissionGuard('manage_accounting'), FeatureFlagGuard],
      data: {
         title: 'Journal Entries',
         titleKH: 'កំណត់ត្រាបញ្ជិកា',
         featureKey: 'accounting',
      },
   },
   {
      path: 'accounts-payable',
      loadComponent: () =>
         import('./accounting/accounts-payable/accounts-payable.component').then(
            (m) => m.AccountsPayableComponent,
         ),
      canActivate: [permissionGuard('manage_accounting'), FeatureFlagGuard],
      data: {
         title: 'Accounts Payable',
         titleKH: 'គណនីត្រូវសង',
         featureKey: 'accounting',
      },
   },
   {
      path: 'accounts-receivable',
      loadComponent: () =>
         import('./accounting/accounts-receivable/accounts-receivable.component').then(
            (m) => m.AccountsReceivableComponent,
         ),
      canActivate: [permissionGuard('manage_accounting'), FeatureFlagGuard],
      data: {
         title: 'Accounts Receivable',
         titleKH: 'គណនីត្រូវទារ',
         featureKey: 'accounting',
      },
   },
   {
      path: 'income-statement',
      loadComponent: () =>
         import('./accounting/reports/income-statement/income-statement.component').then(
            (m) => m.IncomeStatementComponent,
         ),
      canActivate: [permissionGuard('view_accounting'), FeatureFlagGuard],
      data: {
         title: 'Income Statement',
         titleKH: 'របាយការណ៍ប្រាក់ចំណេញ',
         featureKey: 'accounting',
      },
   },
   {
      path: 'balance-sheet',
      loadComponent: () =>
         import('./accounting/reports/balance-sheet/balance-sheet.component').then(
            (m) => m.BalanceSheetComponent,
         ),
      canActivate: [permissionGuard('view_accounting'), FeatureFlagGuard],
      data: {
         title: 'Balance Sheet',
         titleKH: 'តារាងតុល្យការ',
         featureKey: 'accounting',
      },
   },
   {
      path: 'accounting-dashboard',
      loadComponent: () =>
         import('./accounting/accounting-dashboard/accounting-dashboard.component').then(
            (m) => m.AccountingDashboardComponent,
         ),
      canActivate: [permissionGuard('view_accounting'), FeatureFlagGuard],
      data: {
         title: 'Accounting Dashboard',
         titleKH: 'ផ្ទាំងគ្រប់គ្រងគណនេយ្យ',
         featureKey: 'accounting',
      },
   },

   // Old /general-settings links and bookmarks land on the same page.
   { path: 'general-settings', redirectTo: 'settings', pathMatch: 'full' },
   { path: 'general-settings/:page', redirectTo: 'settings/:page' },
   {
      path: 'settings',
      loadComponent: () =>
         import('./settings/settings-hub.component').then(
            (m) => m.SettingsHubComponent,
         ),
      canActivate: [permissionGuard('edit_settings')],
      children: [
         { path: '', redirectTo: 'store-profile', pathMatch: 'full' },
         {
            path: 'store-profile',
            loadComponent: () =>
               import('./settings/store-profile/store-profile.component').then(
                  (m) => m.StoreProfileComponent,
               ),
            data: { title: 'Store Profile', titleKH: 'ព័ត៌មានហាង' },
         },
         {
            path: 'store-settings',
            loadComponent: () =>
               import('./settings/store-settings/store-settings.component').then(
                  (m) => m.StoreSettingsComponent,
               ),
            canActivate: [FeatureFlagGuard],
            data: {
               title: 'Store Settings',
               titleKH: 'ការកំណត់ហាង',
               featureKey: 'core_pos',
            },
         },
         {
            path: 'printers',
            loadComponent: () =>
               import('./settings/printers/printers.component').then(
                  (m) => m.PrintersComponent,
               ),
            canActivate: [FeatureFlagGuard],
            data: {
               title: 'Printer Settings',
               titleKH: 'ការកំណត់ម៉ាស៊ីនបោះពុម្ព',
               featureKey: 'core_pos',
            },
         },
         {
            path: 'delivery-partner',
            loadComponent: () =>
               import('./settings/delivery-partner/delivery-partner.component').then(
                  (m) => m.DeliveryPartnerComponent,
               ),
            canActivate: [deliveryGuard],
            data: { title: 'Delivery Partner', titleKH: 'ដៃគូដឹកជញ្ជូន' },
         },
         {
            path: 'payment-method',
            loadComponent: () =>
               import('./settings/payment-method/payment-method.component').then(
                  (m) => m.PaymentMethodComponent,
               ),
            canActivate: [FeatureFlagGuard],
            data: {
               title: 'Payment Method',
               titleKH: 'ទូទាត់តាមរយៈ',
               featureKey: 'core_pos',
            },
         },
         {
            path: 'exchange-rate',
            loadComponent: () =>
               import('./settings/exchange-rate/exchange-rate.component').then(
                  (m) => m.ExchangeRateComponent,
               ),
            canActivate: [FeatureFlagGuard],
            data: {
               title: 'Exchange Rate',
               titleKH: 'អត្រាប្តូរប្រាក់',
               featureKey: 'core_pos',
            },
         },
         {
            path: 'customer-display-management',
            loadComponent: () =>
               import('./customer-display-management/customer-display-management.component').then(
                  (m) => m.CustomerDisplayManagementComponent,
               ),
            canActivate: [FeatureFlagGuard],
            data: {
               title: 'Customer Display',
               titleKH: 'អេក្រង់អតិថិជន',
               featureKey: 'customer_display',
            },
         },
         {
            path: 'accounting-settings',
            loadComponent: () =>
               import('./accounting/accounting-settings/accounting-settings.component').then(
                  (m) => m.AccountingSettingsComponent,
               ),
            canActivate: [FeatureFlagGuard],
            data: {
               title: 'Accounting Settings',
               titleKH: 'ការកំណត់គណនេយ្យ',
               featureKey: 'accounting',
            },
         },
         {
            path: 'hr-settings',
            loadComponent: () =>
               import('./settings/hr-settings/hr-settings.component').then(
                  (m) => m.HrSettingsComponent,
               ),
            canActivate: [FeatureFlagGuard],
            data: {
               title: 'HR Settings',
               titleKH: 'ការកំណត់ធនធានមនុស្ស',
               featureKey: 'hr',
               requireExplicitFeature: true,
            },
         },
      ],
   },
   {
      path: 'expenses',
      loadComponent: () =>
         import('./expense/expense.component').then((m) => m.ExpenseComponent),
      canActivate: [permissionGuard('create_expense'), FeatureFlagGuard],
      data: { title: 'Expenses', titleKH: 'ចំណាយ', featureKey: 'core_pos' },
   },
   {
      path: 'expenses/history',
      loadComponent: () =>
         import('../cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [permissionGuard('create_expense')],
      data: {
         title: 'Expense Activity Log',
         titleKH: 'ប្រវត្តិចំណាយ',
         sourceFilter: 'Expense',
      },
   },
   {
      path: 'tutorial',
      loadComponent: () =>
         import('./tutorial/tutorial.component').then(
            (m) => m.TutorialComponent,
         ),
      data: { title: 'Tutorial', titleKH: 'មេរៀន' },
   },
];
